import { History } from 'history'
import defaults from 'lodash/defaults'
import qs from 'qs'
import { Dispatch, SetStateAction, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import useSafeState from 'src/commons/hooks/useSafeState'

import { JSONStringify } from '../temporalHelpers'

export default function useUrlState<State extends IQueryParamsObject>(
  defaultState: State,
  jsonFields?: string[]
): [State, Dispatch<SetStateAction<State>>] {
  const history = useHistory()

  /* 
  - We pass a function to useState so that the state is initialized
    after the history object is initialized.
  - The initial state (when the page is first loaded) is an object which is the
    result of a merge between the state from the query params and a default
    state, with priority given to the values of the state from the query params.
  - What happens when you add or modify a query param ? The page is reloaded
    and the initial state is computed.
  */
  const [urlState, setUrlState] = useSafeState<State>(() => {
    const stateFromQueryParams = getObjectFromQueryParams(history.location.search, jsonFields)
    const initialState = defaults(stateFromQueryParams, defaultState)
    overwriteQueryParams(initialState, history, jsonFields)
    return initialState
  })
  /* 
  We have to overwrite query params when :
  - The state changes (of course).
  - But also when location.pathname changes, otherwise we have an old (stale) location object
    used by overwriteQueryParams(...) which causes issues.
  */
  useEffect(() => {
    overwriteQueryParams(urlState, history, jsonFields)
  }, [urlState, history.location.pathname])

  return [urlState, setUrlState]
}

export interface IQueryParamsObject {
  [key: string]: any
}

// Overwrite query params without reloading the page
function overwriteQueryParams(newState: IQueryParamsObject, history: History, jsonFields?: string[]): void {
  history.replace({
    ...history.location,
    search: getQueryParamsFromObject(newState, jsonFields),
  })
}

/*
When converting from React state object to an URL encoded string
with qs.stringify() (https://github.com/ljharb/qs#stringifying), Moment objects
are not properly handled.
JSON manipulation just does the trick by transforming these `moment` objects
into ISO 8601 strings.
*/
function getQueryParamsFromObject(state: IQueryParamsObject, jsonFields?: string[]): string {
  const stateToConvert: IQueryParamsObject = JSON.parse(JSONStringify(state))
  if (jsonFields) {
    for (const jsonField of jsonFields) {
      if (stateToConvert[jsonField]) {
        stateToConvert[jsonField] = JSONStringify(stateToConvert[jsonField])
      }
    }
  }
  return qs.stringify(stateToConvert, {
    arrayFormat: 'brackets',
    indices: false,
  })
}

function getObjectFromQueryParams(queryParamString: string, jsonFields?: string[]): IQueryParamsObject {
  const state: IQueryParamsObject = qs.parse(queryParamString, {
    decoder: customDecoder,
    ignoreQueryPrefix: true,
  })
  if (jsonFields) {
    for (const jsonField of jsonFields) {
      if (state[jsonField]) {
        try {
          state[jsonField] = JSON.parse(state[jsonField])
        } catch (e) {
          console.error(`Error loading field ${jsonField} from queryParams - dropping value ${state[jsonField]}`)
          state[jsonField] = undefined
        }
      }
    }
  }
  return state
}

export function customDecoder(
  value: string,
  defaultDecoder: qs.defaultDecoder,
  _charset: string,
  type: 'key' | 'value'
): any {
  const decodedValue = defaultDecoder(value)
  if (type === 'value') {
    if (value === '') {
      return decodedValue
    }
    const integerValue = Number(value)
    if (!Number.isNaN(integerValue)) {
      return integerValue
    }
  }
  return decodedValue
}
